import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Box, Stack, Typography, IconButton, Chip, Avatar } from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/root-reducer';
import { theme } from '../../../../constants/theme';
import { ThspianAvatar } from '../../../Avatar/ThspianAvatar';
import { Juror } from '../../../../redux/contest/types';
import {
    inviteJury,
    deleteJury,
    emailJuror,
    inviteExternalJuror,
    getJurorForContest,
} from '../../../../redux/jury/action';
import CustomButton from '../../../Button/CustomButton';
import _ from 'lodash';
import { searchUser } from '../../../../redux/user/actions';
import { userSearchSelector, searchingUserSearchSelector } from '../../../AvatarGroup/selector';
import { userProfileSelector } from '../../../../selectors/selectors';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CheckCircle, Pending, Close as CloseIcon } from '@mui/icons-material';
import { Table, ColumnsData } from '../../../../components/Tables/Table';
import { AssignSubmissionsDialogProps } from './AssignSubmissionsDialog';
import { Spinner } from '../../../Loading/Spinner';
import { SegmentedCircle } from '../../../Common/SegmentedCircle';
import { IUserSummary } from '../../../../types/global/helper';
import { IInviteExternalJurorPayload } from '../../../../redux/jury/types';
import {
    brandContestTableCellTextSx,
    brandContestTableShellSx,
    brandJurorSearchDropdownItemSx,
    brandJurorSearchDropdownSx,
    brandJurorSearchFieldSx,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { BrandContestJurorsList } from './BrandContestJurorsList';
import { BRAND_LUXE_PRIMARY } from '../../../../pages/BrandDashboard/brandDashboardTokens';

interface ContestJurorsTabProps {
    jurors: Juror[];
    contestId: string;
    preselectionEnabled?: boolean;
    /** Brand contest manage drawer — pill search + luxe table chrome. */
    visualVariant?: 'default' | 'brand';
}

const AssignSubmissionsDialog = lazy<React.FC<AssignSubmissionsDialogProps>>(() =>
    //@ts-ignore
    import('./AssignSubmissionsDialog').then(module => {
        return { default: module.AssignSubmissionsDialog };
    }),
);

export const ContestJurorsTab: React.FC<ContestJurorsTabProps> = ({
    jurors,
    contestId,
    preselectionEnabled,
    visualVariant = 'default',
}) => {
    const isBrand = visualVariant === 'brand';
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<IUserSummary[]>([]);
    const [, setSearching] = useState(false);
    const [externalEmails, setExternalEmails] = useState<string[]>([]);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);
    const userProfile = useSelector(userProfileSelector);
    const inviteFailTick = useSelector((state: RootState) => state.jury.inviteFailTick ?? 0);
    const alreadySelected = (user: IUserSummary) => {
        return !!jurors.some(jury => jury && jury.juror && jury.juror._id === user._id);
    };
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignDialogJuror, setAssignDialogJuror] = useState<Juror | null>(null);
    const [invitingUserId, setInvitingUserId] = useState<string | null>(null);

    const handleSearch = useRef(
        _.debounce((searchTerm: string) => {
            setSearching(true);
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withJuryQuery: true, contestId }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        // Check if the last character is a comma, indicating a complete email
        if (value.endsWith(',')) {
            const emails = value
                .split(',')
                .map(email => email.trim())
                .filter(email => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(email);
                });

            // If we have a new valid email, add it to the list and clear the input
            const newEmail = emails[emails.length - 1];
            if (newEmail && !externalEmails.includes(newEmail)) {
                setExternalEmails(prev => [...prev, newEmail]);
                setSearchValue(''); // Clear the input after adding email
                return;
            }
        }

        setSearchValue(value);

        // Parse emails from the input for display purposes
        const emails = value
            .split(',')
            .map(email => email.trim())
            .filter(email => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(email);
            });

        // If there are valid emails and no search results, don't search for users
        if (emails.length > 0 && value.includes(',')) {
            return;
        }

        handleSearch(value);
    };

    useEffect(() => {
        if (userResults) {
            setSearchResults(userResults);
            setSearching(false);
        }
    }, [userResults]);

    useEffect(() => {
        dispatch(
            getJurorForContest({
                contestId,
                page: 1,
                limit: 100,
                isRefresh: true,
            }),
        );
    }, [contestId, dispatch]);

    const handleInviteJury = (userId: string) => {
        if (invitingUserId === userId || alreadySelected({ _id: userId } as IUserSummary)) {
            return;
        }
        setInvitingUserId(userId);
        dispatch(inviteJury({ contest: contestId, juries: [userId], bypass: true }));
    };

    useEffect(() => {
        if (!invitingUserId) return;
        if (jurors.some(j => j.juror?._id === invitingUserId)) {
            setInvitingUserId(null);
        }
    }, [jurors, invitingUserId]);

    useEffect(() => {
        if (inviteFailTick > 0) {
            setInvitingUserId(null);
        }
    }, [inviteFailTick]);

    useEffect(() => {
        if (!invitingUserId) return;
        const failSafeTimer = window.setTimeout(() => setInvitingUserId(null), 20000);
        return () => window.clearTimeout(failSafeTimer);
    }, [invitingUserId]);

    const handleAssignSubmissions = (jurorId: string) => {
        const juror = jurors.find(j => j.juror._id === jurorId);
        if (juror && juror.accepted_invite) {
            setAssignDialogJuror(juror);
            setAssignDialogOpen(true);
        }
    };

    const handleCancelDuty = (juryId: string) => {
        const juror = jurors.find(j => j._id === juryId);
        if (juror && juror?.total_assigned > 0) {
            return;
        }
        dispatch(deleteJury({ documentId: juryId }));
    };

    const handleRemoveJuror = (juryId: string) => {
        const juror = jurors.find(j => j._id === juryId);
        if (juror && juror?.total_assigned > 0) {
            return;
        }
        dispatch(deleteJury({ documentId: juryId }));
    };

    const handleEmailJuror = (juryId: string) => {
        dispatch(emailJuror({ userId: juryId, contestId, inviterName: `${userProfile.firstName}` }));
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setSearchResults([]);
        setExternalEmails([]);
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        const updatedEmails = externalEmails.filter(email => email !== emailToRemove);
        setExternalEmails(updatedEmails);
    };

    const handleInviteExternalJurors = () => {
        if (externalEmails.length === 0 || !userProfile) return;

        const payload: IInviteExternalJurorPayload = {
            contest: contestId,
            inviterName: `${userProfile.firstName} ${userProfile.lastName}`,
            externalJuries: externalEmails.map(email => ({
                email,
                name: 'there', // Leave name empty as requested
            })),
        };

        dispatch(inviteExternalJuror(payload));
        handleClearSearch();
    };

    const cellTextSx = isBrand
        ? brandContestTableCellTextSx
        : { fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: theme[themeStyle].tag_white };

    const columnsData: ColumnsData[] = [
        {
            field: 'juror',
            label: 'Juror',
            type: 'custom',
            sortable: true,
            flex: 1,
            customCell: (params: Juror) =>
                params.juror ? (
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ position: 'relative' }}>
                            {params.accepted_invite ? (
                                <CheckCircle
                                    sx={{
                                        position: 'absolute',
                                        top: -5,
                                        left: -5,
                                        color: theme[themeStyle].true_white,
                                        border: `1px solid ${theme[themeStyle].gray_message}`,
                                        borderRadius: '50%',
                                        zIndex: 1,
                                    }}
                                />
                            ) : (
                                <Pending
                                    sx={{
                                        position: 'absolute',
                                        top: -5,
                                        left: -5,
                                        color: theme[themeStyle].tag_white,
                                        border: `1px solid ${theme[themeStyle].gray_message}`,
                                        borderRadius: '50%',
                                        zIndex: 1,
                                    }}
                                />
                            )}
                            <ThspianAvatar
                                user={params.juror}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                }}
                            />
                        </Box>
                        <Stack>
                            <Typography sx={{ ...cellTextSx, fontSize: isBrand ? '0.75rem' : 14 }}>
                                {params.juror.firstName} {params.juror.lastName}
                            </Typography>
                            <Typography
                                sx={{
                                    ...cellTextSx,
                                    fontSize: isBrand ? '0.7rem' : 12,
                                    color: isBrand ? midnight.onSurfaceVariant : theme[themeStyle].tag_white,
                                }}>
                                @{params.juror.userName}
                            </Typography>
                        </Stack>
                    </Stack>
                ) : (
                    <Avatar />
                ),
        },
        {
            field: 'judgment_percentage',
            label: 'Judgment Progress',
            type: 'custom',
            sortable: true,
            width: 200,
            customCell: (params: Juror) => (
                <Box sx={{ width: '100%', alignItems: 'center' }}>
                    <SegmentedCircle
                        percentage={params.judgment_percentage}
                        textColor={isBrand ? midnight.onSurface : theme[themeStyle].tag_white}
                        size={32}
                        segments={28}
                        strokeWidth={0.9}
                        tickLength={5}
                        fontSizeRatio={0.2}
                    />
                </Box>
            ),
        },
        {
            field: 'total_assigned',
            label: 'Total Assigned',
            type: 'text',
            sortable: true,
            width: 150,
        },
        {
            field: 'judged_submissions',
            label: 'Completed',
            type: 'text',
            sortable: true,
            width: 100,
        },
        {
            field: 'accepted_invite',
            label: 'Invitation Status',
            type: 'custom',
            sortable: true,
            width: 150,
            customCell: (params: Juror) => (
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: params.accepted_invite ? theme[themeStyle].true_black : theme[themeStyle].tag_white,
                        fontSize: 12,
                        backgroundColor: params.accepted_invite
                            ? theme[themeStyle].switchGreen
                            : theme[themeStyle].red_status_indicator,
                        padding: 10,
                        borderRadius: 1,
                    }}>
                    {params.accepted_invite ? 'Accepted' : 'Pending'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'action' as const,
            width: 100,
            sortable: false,
        },
    ];

    const moreMenuItems = [
        { title: 'Assign Submissions', id: 'assign-submission' },
        { title: 'Cancel Duty', id: 'cancel-duty' },
        { title: 'Remove Juror', id: 'remove-juror' },
        { title: 'Email Juror', id: 'email-juror' },
    ];

    const handleJurorMenuClick = (type: string, row: Juror) => {
        if (type === 'assign-submission') handleAssignSubmissions(row.juror._id);
        if (type === 'cancel-duty') handleCancelDuty(row._id);
        if (type === 'remove-juror') handleRemoveJuror(row._id);
        if (type === 'email-juror') handleEmailJuror(row.juror._id);
    };

    return (
        <Box>
            {/* Search input and dropdown container */}
            <Box
                sx={{
                    position: 'relative',
                    width: isBrand ? '100%' : { xs: '100%', sm: '60%' },
                    mb: 2,
                }}>
                <Stack
                    direction={isBrand ? { xs: 'column', lg: 'row' } : { xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={isBrand ? 'stretch' : 'center'}>
                    <Box
                        sx={
                            isBrand
                                ? {
                                      ...brandJurorSearchFieldSx,
                                      width: '100%',
                                      flex: '1 1 auto',
                                      minWidth: 0,
                                  }
                                : {
                                      flex: 1,
                                      position: 'relative',
                                      background: theme[themeStyle].deep_grey_lighter,
                                      borderRadius: 2,
                                      border: `1px solid ${theme[themeStyle].tag_white}`,
                                      minHeight: 50,
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '8px 12px',
                                      '&:hover': {
                                          borderColor: theme[themeStyle].highlight,
                                      },
                                      '&:focus-within': {
                                          borderColor: theme[themeStyle].highlight,
                                      },
                                  }
                        }>
                        {/* Email chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mr: 1 }}>
                            {externalEmails.map((email, index) => (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => handleRemoveEmail(email)}
                                    deleteIcon={<CloseIcon sx={{ color: theme[themeStyle].red_status_indicator }} />}
                                    size="small"
                                    sx={
                                        isBrand
                                            ? {
                                                  bgcolor: midnight.surfaceContainer,
                                                  borderRadius: '8px',
                                                  color: midnight.onSurface,
                                                  fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                  fontSize: '0.7rem',
                                                  height: 26,
                                                  border: '1px solid rgba(255,201,101,0.2)',
                                                  '& .MuiChip-deleteIcon': {
                                                      color: midnight.onSurfaceVariant,
                                                      fontSize: '14px',
                                                  },
                                              }
                                            : {
                                                  backgroundColor: theme[themeStyle].secondaryBg,
                                                  borderRadius: 1,
                                                  color: theme[themeStyle].tag_white,
                                                  fontSize: '11px',
                                                  height: 24,
                                                  '& .MuiChip-deleteIcon': {
                                                      color: theme[themeStyle].true_white,
                                                      fontSize: '14px',
                                                  },
                                                  '& .MuiChip-label': {
                                                      paddingLeft: '8px',
                                                      paddingRight: '4px',
                                                  },
                                              }
                                    }
                                />
                            ))}
                        </Box>

                        {/* Text input */}
                        <input
                            type="text"
                            value={searchValue}
                            onChange={handleSearchInputChange}
                            placeholder={
                                externalEmails.length > 0
                                    ? 'Add more emails...'
                                    : 'Search and add jurors or enter emails separated by comma to invite external users'
                            }
                            style={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: isBrand ? midnight.onSurface : theme[themeStyle].tag_white,
                                fontSize: isBrand ? '0.9375rem' : '14px',
                                minWidth: 0,
                            }}
                        />

                        {/* Clear button */}
                        {searchValue && (
                            <IconButton
                                onClick={handleClearSearch}
                                size="small"
                                sx={{
                                    color: isBrand ? midnight.onSurfaceVariant : theme[themeStyle].tag_white,
                                    padding: '4px',
                                    ml: 1,
                                }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    {externalEmails.length > 0 && (
                        <CustomButton
                            onClick={handleInviteExternalJurors}
                            label="Invite External Jurors"
                            customStyle={{
                                minWidth: isBrand ? undefined : 150,
                                width: isBrand ? '100%' : undefined,
                                fontSize: 12,
                                padding: '8px 16px',
                                height: 50,
                            }}
                        />
                    )}
                </Stack>
                {/* Dropdown for search results */}
                {searchValue.length > 2 && searchResults.length > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '100%',
                            zIndex: 10,
                            ...(isBrand
                                ? brandJurorSearchDropdownSx
                                : {
                                      background: theme[themeStyle].deep_grey_lighter,
                                      border: `1px solid ${theme[themeStyle].gray_message}`,
                                      borderRadius: 2,
                                      boxShadow: 3,
                                      mt: 1,
                                      maxHeight: 200,
                                      overflowY: 'auto',
                                  }),
                        }}>
                        {searchResults.map((user: IUserSummary, idx: number) => (
                            <Stack
                                key={user._id || idx}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={
                                    isBrand
                                        ? brandJurorSearchDropdownItemSx
                                        : {
                                              px: 2,
                                              py: 1,
                                              cursor: 'pointer',
                                              '&:hover': {
                                                  background: theme[themeStyle].gray_message,
                                              },
                                          }
                                }>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <ThspianAvatar user={user} sx={{ width: 32, height: 32 }} />
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: isBrand ? midnight.onSurface : theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: isBrand ? '0.875rem' : 14,
                                            }}>
                                            {user.firstName} {user.lastName}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: isBrand
                                                    ? midnight.onSurfaceVariant
                                                    : theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: isBrand ? '0.75rem' : 12,
                                            }}>
                                            @{user.userName}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <CustomButton
                                    onClick={() => handleInviteJury(user._id)}
                                    label={alreadySelected(user) ? 'Invited' : 'Invite'}
                                    loading={invitingUserId === user._id && !alreadySelected(user)}
                                    disabled={alreadySelected(user) || invitingUserId === user._id}
                                    customStyle={
                                        isBrand
                                            ? {
                                                  minWidth: 72,
                                                  fontSize: 11,
                                                  padding: '4px 12px',
                                                  backgroundColor: BRAND_LUXE_PRIMARY,
                                                  color: '#0e0e0e',
                                                  fontFamily: FONT_ENUM.MAIN_BOLD,
                                              }
                                            : { minWidth: 70, fontSize: 12, padding: '2px 8px' }
                                    }
                                    textStyle={isBrand ? { color: '#0e0e0e' } : undefined}
                                    sx={
                                        isBrand
                                            ? {
                                                  border: 'none',
                                                  '&:hover': {
                                                      backgroundColor: BRAND_LUXE_PRIMARY,
                                                      filter: 'brightness(0.92)',
                                                      color: '#0e0e0e',
                                                      WebkitTextFillColor: '#0e0e0e',
                                                  },
                                                  '&:hover .MuiTypography-root': {
                                                      color: '#0e0e0e',
                                                  },
                                              }
                                            : undefined
                                    }
                                />
                            </Stack>
                        ))}
                    </Box>
                )}
            </Box>
            <Box
                sx={
                    isBrand
                        ? brandContestTableShellSx
                        : {
                              backgroundColor: theme[themeStyle].secondaryBg,
                              borderRadius: 2,
                              padding: 3,
                              '& .MuiDataGrid-root': {
                                  backgroundColor: theme[themeStyle].secondaryBg,
                              },
                              '& .MuiDataGrid-filler': {
                                  backgroundColor: theme[themeStyle].secondaryBg,
                              },
                              '& .MuiDataGrid-virtualScroller': {
                                  backgroundColor: theme[themeStyle].secondaryBg,
                              },
                          }
                }>
                {isBrand ? (
                    <BrandContestJurorsList
                        jurors={jurors}
                        menuItems={moreMenuItems}
                        onMenuClick={handleJurorMenuClick}
                    />
                ) : (
                    <Table
                        columns={columnsData}
                        staticData={jurors}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        tableType="other"
                        rowHeight={80}
                        headerHeight={60}
                        onFullscreenMenuClick={(type, row) => handleJurorMenuClick(type, row)}
                        fullscreenMoreMenu={moreMenuItems}
                        fullscreenMoreIcon
                        textSx={{ fontSize: 14 }}
                        sx={{
                            minHeight: 450,
                            backgroundColor: theme[themeStyle].secondaryBg,
                            '& .MuiDataGrid-root': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .MuiDataGrid-filler': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                        }}
                    />
                )}
            </Box>
            {/* Assign Submissions Dialog */}
            <Suspense fallback={<Spinner />}>
                {assignDialogJuror && (
                    <AssignSubmissionsDialog
                        open={assignDialogOpen}
                        onClose={() => setAssignDialogOpen(false)}
                        juror={assignDialogJuror}
                        contestId={contestId}
                        preselectionEnabled={preselectionEnabled}
                        visualVariant={isBrand ? 'brand' : 'default'}
                    />
                )}
            </Suspense>
        </Box>
    );
};
